import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { PLAYBOOK_TYPES } from '../types/picks';
import { fetchUpcomingEvents, plainName } from '../utils/upcomingEvents';

const PLAYBOOKS = Object.values(PLAYBOOK_TYPES);

const titleCase = (type) => type.charAt(0).toUpperCase() + type.slice(1);

// A parlay's names, written "A, B & C", as a list (the same split the data job uses, publish.py _names)
const names = (text) => String(text || '').replace(/&/g, ',').split(',').map(x => x.trim()).filter(Boolean);

// A fight is the same whichever side was picked
const fightKey = (a, b) => [a, b].sort().join(' | ');

// Every fight any playbook bets on at the event, with what each playbook picks in it.
// Fights come only from the picks, so a fight no playbook bets on is not listed.
const buildFights = (playbooks, eventId) => {
  const fights = new Map();
  PLAYBOOKS.forEach(type => {
    (playbooks[type]?.picks || [])
      .filter(pick => pick.event?.id === eventId)
      .forEach(pick => {
        const isParlay = pick.type === 'parlay';
        const fighters = isParlay ? names(pick.pick_name) : [String(pick.pick_name || '').trim()];
        const opponents = isParlay ? names(pick.opponent_name) : [String(pick.opponent_name || '').trim()];
        if (fighters.length !== opponents.length) return;
        fighters.forEach((fighter, i) => {
          const opponent = opponents[i];
          if (!fighter || !opponent) return;
          const key = fightKey(fighter, opponent);
          if (!fights.has(key)) fights.set(key, { key, fighters: [fighter, opponent], picks: {} });
          const byPlaybook = fights.get(key).picks;
          byPlaybook[type] = byPlaybook[type] || {};
          const choice = byPlaybook[type][fighter] || (byPlaybook[type][fighter] = { stake: 0, parlays: 0 });
          if (isParlay) choice.parlays += 1;
          else choice.stake += pick.budget_amount || pick.allocation || 0;
        });
      });
  });
  return Array.from(fights.values());
};

// The fights in the order of the published card (main event first), each fighter on the side the card lists
// them. A fight the card does not hold (no card published yet, or a name spelled differently) goes last.
const inCardOrder = (fights, card) => {
  const position = new Map();
  (card || []).forEach((bout, index) => {
    const order = bout.order ?? index;
    position.set(fightKey(plainName(bout.fighter_name), plainName(bout.opponent_name)), {
      order, first: plainName(bout.fighter_name),
    });
  });
  return fights
    .map(fight => {
      const found = position.get(fightKey(plainName(fight.fighters[0]), plainName(fight.fighters[1])));
      if (found && plainName(fight.fighters[1]) === found.first) {
        fight = { ...fight, fighters: [fight.fighters[1], fight.fighters[0]] };
      }
      return { fight, order: found ? found.order : Infinity };
    })
    .sort((a, b) => a.order - b.order)
    .map(({ fight }) => fight);
};

// The share of the playbooks betting on a fight that back the same fighter. A playbook backing
// both sides counts among those betting but backs neither, so it lowers the agreement.
const agreement = (fight) => {
  const betting = Object.values(fight.picks);
  const backing = fight.fighters.map(fighter => betting.filter(choices => Object.keys(choices).length === 1 && choices[fighter]).length);
  const top = backing[0] >= backing[1] ? 0 : 1;
  return { share: backing[top] / betting.length, count: backing[top], total: betting.length, fighter: fight.fighters[top] };
};

const choiceNote = ({ stake, parlays }) => [
  stake > 0 && (stake < 0.0005 ? '<0.1%' : `${(stake * 100).toFixed(1)}%`),
  parlays > 0 && `${parlays} parlay${parlays === 1 ? '' : 's'}`,
].filter(Boolean).join(' + ');

export default function PlaybookComparison() {
  const [playbooks, setPlaybooks] = useState({});
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [loading, setLoading] = useState(true);
  const [cards, setCards] = useState({});

  // The published cards give the fights their order; without one the table keeps the order the picks come in
  useEffect(() => {
    fetchUpcomingEvents()
      .then(evts => setCards(Object.fromEntries(evts.map(evt => [evt.id, evt.fights]))))
      .catch(error => console.error('Error fetching upcoming events:', error));
  }, []);

  useEffect(() => {
    async function fetchPlaybooks() {
      setLoading(true);
      try {
        // A playbook not published yet reads as permission denied (the rules check the missing document),
        // so each one loads on its own and a failed one is left out of the table
        const results = await Promise.allSettled(PLAYBOOKS.map(type => getDoc(doc(db, 'playbooks', `odikai_${type}`))));
        const loaded = {};
        const eventMap = new Map();
        results.forEach((result, i) => {
          if (result.status !== 'fulfilled' || !result.value.exists()) return;
          const data = result.value.data();
          // Playbooks the admin has hidden are left out
          if (data.is_public === false) return;
          loaded[PLAYBOOKS[i]] = data;
          (data.picks || []).forEach(pick => {
            if (pick.event && !eventMap.has(pick.event.id)) {
              eventMap.set(pick.event.id, { id: pick.event.id, name: pick.event.name, date: pick.event.date });
            }
          });
        });
        const derivedEvents = Array.from(eventMap.values()).sort((a, b) => new Date(a.date) - new Date(b.date));
        setPlaybooks(loaded);
        setEvents(derivedEvents);
        setSelectedEventId(prev => (derivedEvents.find(e => e.id === prev) ? prev : derivedEvents[0]?.id || ''));
      } catch (error) {
        console.error('Error fetching playbooks:', error);
        setPlaybooks({});
        setEvents([]);
      }
      setLoading(false);
    }

    fetchPlaybooks();
  }, []);

  const selectedEvent = events.find(e => e.id === selectedEventId);
  const fights = inCardOrder(buildFights(playbooks, selectedEventId), cards[selectedEventId]);
  const columns = PLAYBOOKS.filter(type => playbooks[type]);

  return (
    <section className="playbook-compare" aria-live="polite">
      <div className="compare-head">
        <div className="control-block">
          <label className="field-label" htmlFor="compare-event">Event</label>
          <select
            id="compare-event"
            value={selectedEventId}
            onChange={(e) => setSelectedEventId(e.target.value)}
            className="selector-input"
          >
            {events.map(evt => (
              <option key={evt.id} value={evt.id}>{evt.name}</option>
            ))}
          </select>
        </div>
        <p className="control-note">
          Who each playbook is backing in every fight it bets on. Each cell shows the share of the budget on that
          fighter to win outright, and how many parlays include them.
        </p>
      </div>

      {loading ? (
        <div className="loading"><div className="loading-spinner"></div>Loading picks…</div>
      ) : fights.length > 0 ? (
        <div className="playbooks-table-container compare-table-container">
          <table className="playbooks-table compare-table">
            <caption className="visually-hidden">Picks by playbook for {selectedEvent?.name}</caption>
            <thead>
              <tr>
                <th scope="col">Fight</th>
                {columns.map(type => <th key={type} scope="col">{titleCase(type)}</th>)}
              </tr>
            </thead>
            <tbody>
              {fights.map(fight => {
                const agreed = agreement(fight);
                return (
                <tr key={fight.key}>
                  <th scope="row">
                    {fight.fighters.map((fighter, i) => (
                      <React.Fragment key={fighter}>
                        {i === 1 && <span className="compare-vs">vs</span>}
                        <span className={`compare-fighter${agreed.share === 1 && fighter === agreed.fighter ? ' agreed' : ''}`}>
                          {fighter}
                        </span>
                      </React.Fragment>
                    ))}
                    <span
                      className={`compare-agreement${agreed.share === 1 ? ' full' : ''}`}
                      title={`${agreed.count} of ${agreed.total} playbook${agreed.total === 1 ? '' : 's'} betting on this fight back ${agreed.fighter}`}
                    >
                      {Math.round(agreed.share * 100)}% agree
                    </span>
                  </th>
                  {columns.map(type => {
                    const choices = fight.picks[type] || {};
                    const picked = fight.fighters.filter(fighter => choices[fighter]);
                    return (
                      <td key={type}>
                        {picked.length === 0 ? (
                          <span className="compare-none" aria-label="No pick">—</span>
                        ) : picked.map(fighter => (
                          <div
                            key={fighter}
                            className={`compare-pick ${fighter === fight.fighters[0] ? 'side-a' : 'side-b'}`}
                          >
                            <span className="compare-pick-name">{fighter}</span>
                            <span className="compare-pick-note">{choiceNote(choices[fighter])}</span>
                          </div>
                        ))}
                      </td>
                    );
                  })}
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-state">No picks available for this event.</div>
      )}
    </section>
  );
}
